import type { AnyAbility, RawRuleOf } from '@casl/ability'
import { AbilityBuilder, createAliasResolver, PureAbility } from '@casl/ability'
import { getTableById } from '@pkg/db/utils'
import type { AtLeast } from '@pkg/shared/types/helpers'
import type { DeepKeys } from '@tanstack/form-core'

export type CRUD = 'create' | 'read' | 'update' | 'delete' | 'restore'
export type CrudWithAlias = CRUD | 'modify'
export type CrudNameAbilities<DB> = {
  [K in keyof DB]: [CrudWithAlias, K]
}[keyof DB]
type CrudValueAbilities<DB> = {
  // @ts-expect-error ignore. it always has an id and making it typesafe is to complex
  [K in keyof DB]: [CrudWithAlias, AtLeast<DB[K], 'id'>]
}[keyof DB]
export type CrudAbilities<DB> = CrudNameAbilities<DB> | CrudValueAbilities<DB>

type AppMatchCondition<TData> = (data: TData) => boolean

interface AppRuleResult {
  because(reason: string): void
}

export interface AppAbilityBuilder<DB> extends Omit<AbilityBuilder<AnyAbility>, 'can' | 'cannot'> {
  can: <A extends CRUD, TName extends keyof DB, TData = DB[TName], TField extends string = DeepKeys<TData>>(
    action: A,
    subject: TName,
    field?: TField | AppMatchCondition<TData>,
    condition?: AppMatchCondition<TData>,
  ) => AppRuleResult
  cannot: <A extends CRUD, TName extends keyof DB, TData = DB[TName], TField extends string = DeepKeys<TData>>(
    action: A,
    subject: TName,
    field?: TField | AppMatchCondition<TData>,
    condition?: AppMatchCondition<TData>,
  ) => AppRuleResult
  build: (options?: Parameters<AbilityBuilder<AnyAbility>['build']>[0]) => AnyAbility
  rules: RawRuleOf<AnyAbility>[]
}

export interface AppAbilityBuilderCallbackOptions<DB> {
  builder: AppAbilityBuilder<DB>
}

export type AppAbilityBuilderCallback<DB> = (opts: AppAbilityBuilderCallbackOptions<DB>) => void

export const abilityFactory = <DB>(cb: AppAbilityBuilderCallback<DB>) => {
  const { can: caslCan, cannot: caslCannot, build, rules } = new AbilityBuilder(PureAbility as never)

  const builder: AppAbilityBuilder<DB> = {
    can: caslCan,
    cannot: caslCannot,
    build,
    rules,
  }

  cb({ builder })

  return builder.build({
    conditionsMatcher: matchConditions => {
      return subject => {
        if (typeof matchConditions === 'function') {
          return matchConditions(subject)
        }
        // Fallback to basic equality for standard CASL conditions if needed,
        // though our builder mainly uses functions here.
        return true
      }
    },
    resolveAction: createAliasResolver({
      modify: ['update', 'delete'],
    }),
    fieldMatcher: fields => {
      return field => {
        // noinspection RegExpRedundantEscape
        const normalizedFieldName = field.replace(/\[\d+\]/g, '')
        return fields.includes(normalizedFieldName as never)
      }
    },
    detectSubjectType(subject) {
      // noinspection SuspiciousTypeOfGuard
      if (typeof subject === 'string') {
        return subject
      }
      if (subject && typeof subject === 'object' && 'id' in subject && subject.id) {
        return getTableById(subject.id)
      }
      throw new Error(`Cannot detect subject type for ${JSON.stringify(subject)}`)
    },
  })
}
